class HomeValueSection extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedToScroller) return;

    this.shelf = this.querySelector('.home-value-section__shelf');
    this.previousButton = this.querySelector('.home-value-section__scroll-button--prev');
    this.nextButton = this.querySelector('.home-value-section__scroll-button--next');
    this.progress = this.querySelector('.home-value-section__pager');
    this.progressThumb = this.querySelector('.home-value-section__pager-thumb');

    if (!this.shelf || !this.previousButton || !this.nextButton || !this.progress || !this.progressThumb) return;

    this.isConnectedToScroller = true;
    this.updateFrame = 0;
    this.scheduleUpdate = this.scheduleUpdate.bind(this);
    this.scrollPrevious = () => this.scrollShelf(-1);
    this.scrollNext = () => this.scrollShelf(1);

    this.previousButton.addEventListener('click', this.scrollPrevious);
    this.nextButton.addEventListener('click', this.scrollNext);
    this.shelf.addEventListener('scroll', this.scheduleUpdate, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.resizeObserver.observe(this.shelf);
    } else {
      window.addEventListener('resize', this.scheduleUpdate, { passive: true });
    }

    this.updateControls();
  }

  disconnectedCallback() {
    if (!this.isConnectedToScroller) return;

    if (this.updateFrame) window.cancelAnimationFrame(this.updateFrame);
    this.previousButton.removeEventListener('click', this.scrollPrevious);
    this.nextButton.removeEventListener('click', this.scrollNext);
    this.shelf.removeEventListener('scroll', this.scheduleUpdate);

    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener('resize', this.scheduleUpdate);

    this.isConnectedToScroller = false;
  }

  updateControls() {
    this.updateFrame = 0;

    const maxScroll = Math.max(0, this.shelf.scrollWidth - this.shelf.clientWidth);
    const scrollPosition = Math.min(maxScroll, Math.max(0, this.shelf.scrollLeft));
    const isScrollable = maxScroll > 2;

    this.classList.toggle('home-value-section--scrollable', isScrollable);
    this.previousButton.disabled = !isScrollable || scrollPosition <= 2;
    this.nextButton.disabled = !isScrollable || scrollPosition >= maxScroll - 2;

    const trackWidth = this.progress.clientWidth;
    if (!isScrollable || trackWidth <= 0) return;

    const thumbWidth = Math.max(18, trackWidth * (this.shelf.clientWidth / this.shelf.scrollWidth));
    const thumbTravel = Math.max(0, trackWidth - thumbWidth);
    const progressRatio = maxScroll > 0 ? scrollPosition / maxScroll : 0;

    this.progressThumb.style.width = `${thumbWidth}px`;
    this.progressThumb.style.transform = `translateX(${thumbTravel * progressRatio}px)`;
  }

  scheduleUpdate() {
    if (this.updateFrame) return;
    this.updateFrame = window.requestAnimationFrame(() => this.updateControls());
  }

  scrollShelf(direction) {
    const firstCard = this.shelf.querySelector('.home-value-section__card');
    const shelfStyles = window.getComputedStyle(this.shelf);
    const gap = Number.parseFloat(shelfStyles.columnGap || shelfStyles.gap) || 0;
    const cardStep = firstCard ? firstCard.getBoundingClientRect().width + gap : this.shelf.clientWidth * 0.75;
    const visibleCards = Math.max(1, Math.floor(this.shelf.clientWidth / cardStep) - 1);
    const maxScroll = Math.max(0, this.shelf.scrollWidth - this.shelf.clientWidth);
    const targetScroll = Math.min(maxScroll, Math.max(0, this.shelf.scrollLeft + direction * cardStep * visibleCards));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.shelf.scrollTo({
      left: targetScroll,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('home-value-section')) {
  customElements.define('home-value-section', HomeValueSection);
}
